import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { tt } from '@/internationalization/i18n'
import { toast } from '@/stores'
import type { ActionItem, ActionItemStatus, Paginated } from '@/types'
import { actionsApi, type ActionItemPatch, type ActionListParams } from './api'

export function useActionItems(params: ActionListParams) {
  return useQuery({
    queryKey: qk.actions.list(params),
    queryFn: () => actionsApi.list(params),
  })
}

/**
 * The meetings the "filter by meeting" control offers.
 *
 * One request, cached, filtered in the browser — the same shape as the
 * assignee picker and for the same reason: a person typing into a filter
 * expects it to narrow as they type, not after a round trip. Fetched only when
 * the control is opened, because the action list itself does not need it.
 */
export function useActionItemMeetings(enabled: boolean) {
  return useQuery({
    queryKey: qk.actions.meetings,
    queryFn: () => actionsApi.meetings(),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Who this workspace can hand a commitment to.
 *
 * Fetched only when a picker actually opens: it is one round trip per meeting
 * and the answer is the roster plus the member list, neither of which a list
 * view needs in order to render.
 */
export function useAssigneeCandidates(meetingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: qk.actions.assignees(meetingId),
    queryFn: () => actionsApi.assignees(meetingId),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Every message the action-item routes can refuse with, in one place.
 *
 * `assignee_not_found` and `assignee_inactive` are the two a person can act on
 * — the first means "type their email instead", the second means "they are
 * suspended" — so they must not collapse into a generic "could not save".
 */
function explain(error: unknown, fallbackTitle: string) {
  if (error instanceof ApiError) {
    if (error.code === 'action_item_changed') {
      return toast.error(
        tt('actions.toast.itemChangedTitle'),
        tt('actions.toast.itemChangedBody'),
      )
    }
    if (error.code === 'assignee_not_found') {
      return toast.error(
        tt('actions.toast.notFoundTitle'),
        tt('actions.toast.notFoundBody'),
      )
    }
    if (
      error.code === 'assignee_inactive' ||
      error.code === 'invalid_assignee_email' ||
      error.code === 'action_item_is_extracted' ||
      error.code === 'assignee_may_only_report_progress'
    ) {
      return toast.error(fallbackTitle, error.message)
    }
  }
  return toast.error(fallbackTitle, tt('actions.toast.changeNotSaved'))
}

/**
 * Ticking off an action item is optimistic: the checkbox must respond to the
 * tap, not to the round trip. On failure the previous list is put back and a
 * toast says so — silently reverting looks like the tap was never registered.
 */
export function useSetActionStatus(params: ActionListParams) {
  const queryClient = useQueryClient()
  const queryKey = qk.actions.list(params)

  return useMutation({
    mutationFn: ({ id, status, rowVersion }: { id: string; status: ActionItemStatus; rowVersion?: number }) =>
      actionsApi.setStatus(id, status, rowVersion),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Paginated<ActionItem>>(queryKey)
      queryClient.setQueryData<Paginated<ActionItem>>(queryKey, (current) =>
        current
          ? { ...current, items: current.items.map((item) => (item.id === id ? { ...item, status } : item)) }
          : current,
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous)
      explain(error, tt('actions.toast.updateFail'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: qk.actions.all })
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
    },
  })
}

/**
 * Editing the text, the due date or the assignee.
 *
 * NOT optimistic, unlike the checkbox, and the difference is deliberate: this
 * one can be refused for reasons only the server knows (the address belongs to
 * nobody, the person is suspended, somebody else edited it first), and an
 * optimistic assignee that vanishes a second later reads as the product
 * reassigning the item on its own.
 */
export function useUpdateActionItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
      rowVersion,
    }: {
      id: string
      patch: ActionItemPatch
      rowVersion?: number
    }) => actionsApi.update(id, patch, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.actions.all })
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
    },
    onError: (error) => explain(error, tt('actions.toast.saveFail')),
  })
}

export function useCreateActionItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: actionsApi.create,
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: qk.actions.all })
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
      toast.success(
        tt('actions.toast.addedTitle'),
        item.assignee
          ? tt('actions.toast.addedAssigned', { name: item.assignee.displayName })
          : tt('actions.toast.addedUnassigned'),
      )
    },
    onError: (error) => explain(error, tt('actions.toast.addFail')),
  })
}

/**
 * Deleting works only on items somebody typed.
 *
 * An extracted item comes back 409 and is dismissed instead — its text has
 * usually already gone out in the summary email, so removing the row would make
 * the product disagree with something people have in their inbox.
 */
export function useDeleteActionItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => actionsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.actions.all })
      queryClient.invalidateQueries({ queryKey: qk.meetings.all })
    },
    onError: (error) => explain(error, tt('actions.toast.deleteFail')),
  })
}
